import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceKey';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { AngleDownIcon, AngleUpIcon, CheckCircleIcon } from 'assets/icons';
import {
  setReferencesIsSelected,
  updateAllUrgencyFlagsRequest,
  updateOrderMarkRequest,
  updateReferenceUrgencyFlag,
} from 'domains/basket/Basket.store';
import { VehicleLocal } from 'domains/basket/Basket.types';
import { VehicleCountry } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { fetchKnownVehicle } from 'domains/catalog/fetchKnownVehicle';
import { collectAllReferences } from 'domains/order/Order.mapper';
import { getOrderMarkMaxLength } from 'domains/order/Order.utils';
import { getDiscounts, getLoadedPrices } from 'domains/references';
import { getUserCommercialLink, getUserCountry } from 'domains/user';
import CartReferenceTable from 'pages/CartPage/CartStep/CartItems/CartReferenceTable';
import { calculateTotalPrice } from 'pages/CartPage/CartStep/CartStep';
import { theme } from 'styles';
import { Checkbox, Flex, Icon, MarginBox, Text, WhiteButton } from 'UI';
import VehicleFlag from 'UI/Icon/VehicleFlag';
import { textFormatter } from 'utils/format';
import { formatVehicleBrand } from 'utils/mappers';
import { CartItemWrapper, SInput, UrgentWrapper } from '../CartItems.styled';

interface CartItemsProps {
  basketVehicle: VehicleLocal;
  currency?: string;
  markRemovedReferences: (numberOfReferences: number) => void;
  disabledRefs: string[];
  highlightedReference: ReferenceKey | undefined;
  setDisableRefs: (ref: string) => void;
  unsetDisableRef: (ref: string) => void;
}

const CartItemsVehicle = ({
  basketVehicle,
  currency,
  markRemovedReferences,
  disabledRefs,
  highlightedReference,
  setDisableRefs,
  unsetDisableRef,
}: CartItemsProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [isSelectAllUrgentChecked, setAllUrgentChecked] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const refNumbers = basketVehicle.references.map((r) => r.referenceNumber);
  const discounts = useSelector((state: RootState) => getDiscounts(state, refNumbers));
  const loadedPrices = useSelector((state: RootState) => getLoadedPrices(state, refNumbers));
  const totalPriceForVehicle = calculateTotalPrice(basketVehicle?.references, discounts, loadedPrices);
  const params = new URLSearchParams(location.search);
  const commercialLink = useSelector(getUserCommercialLink);
  const isAllVehicleRefUrgent = basketVehicle.references.every((r) => r.isUrgent);
  const userCountry = useSelector(getUserCountry);
  const orderMarkMaxLength = getOrderMarkMaxLength(userCountry);

  function setVehiclesReferences(isSelected: boolean) {
    const refs = collectAllReferences(basketVehicle.references);
    dispatch(
      setReferencesIsSelected({
        isSelected,
        referenceIds: refs,
        vehicleKey: basketVehicle.vehicleDetail.vehicleKey,
      }),
    );
  }

  function doSelectAll(isSelected: boolean) {
    setVehiclesReferences(isSelected);
  }

  function selectAll(isSelected: boolean) {
    doSelectAll(isSelected);
  }

  function selectAllUrgent() {
    dispatch(
      updateAllUrgencyFlagsRequest({
        basketReferenceType: basketVehicle.vehicleDetail.vehicleKey ? 'VEHICLE' : 'OTHER',
        vehicleKey: basketVehicle.vehicleDetail.vehicleKey,
        externalBasketId: undefined,
        isUrgent: !isSelectAllUrgentChecked,
      }),
    );
    dispatch(
      updateReferenceUrgencyFlag({
        vehicleKey: basketVehicle.vehicleDetail.vehicleKey,
        externalBasketId: undefined,
        referencesToUpdate: basketVehicle.references.map((r) => {
          return { referenceNumber: r.referenceNumber, sellerNumber: r.sellerNumber };
        }),
        isUrgent: !isSelectAllUrgentChecked,
      }),
    );
    setAllUrgentChecked(!isSelectAllUrgentChecked);
  }

  useEffect(() => {
    setAllUrgentChecked(isAllVehicleRefUrgent);
  }, [isAllVehicleRefUrgent]);

  const selectedReferencesCount = basketVehicle.references.filter((reference) => reference.isSelected).length;
  const isSelectAllChecked = selectedReferencesCount > 0 && selectedReferencesCount === basketVehicle.references.length;

  const bmmVehicleHeader = () => {
    const vehicle = basketVehicle.vehicleDetail;
    if (vehicle?.catalogSource === 'IAM') {
      return (
        <Flex direction={'row'}>
          <Text type="base" data-cy={'car-name-iam'}>
            {formatVehicleBrand(vehicle.vehicleBrand) ?? ''} {vehicle.model ?? ''} {vehicle.iamVehicle?.phase ?? ''}
          </Text>
        </Flex>
      );
    }
    return (
      <>
        <Flex direction={'row'}>
          <Text type="base">
            {vehicle?.dataHubVehicle?.name} {vehicle?.dataHubVehicle?.modelType}
          </Text>
        </Flex>
        <Flex direction={'row'}>
          <Text type="sm" color="grey35">
            {vehicle?.dataHubVehicle?.engine} - {vehicle.dataHubVehicle?.gearbox}
          </Text>
        </Flex>
      </>
    );
  };

  const vehicleHeader = (vehicleDetail: VehicleDetail) => {
    switch (vehicleDetail.catalogSource) {
      case 'DATAHUB':
        return (
          <>
            <Flex direction={'row'} data-cy={'car-name-dh'}>
              <Text type="base">{vehicleDetail?.dataHubVehicle?.name}</Text>
              <MarginBox ml={5} mr={5} mt={2}>
                <VehicleFlag vehicle={vehicleDetail} />
              </MarginBox>
              <Text type="base" data-cy={'vrn'}>
                {vehicleDetail?.vrn}
              </Text>
            </Flex>
            <Flex direction={'row'}>
              <Text type="sm" color="grey35" data-cy={'vin'}>
                {`${t('catalog.vehicle.vin', 'VIN')}: ${vehicleDetail?.vin}`}
              </Text>
            </Flex>
          </>
        );
      case 'IAM':
        return (
          <>
            <Flex direction={'row'} data-cy={'car-name-iam'}>
              <Text type="base">
                {`${formatVehicleBrand(vehicleDetail.vehicleBrand) ?? vehicleDetail.iamVehicle?.name ?? ''} ${
                  vehicleDetail.model
                }`}
              </Text>
              <MarginBox ml={5} mr={5} mt={2}>
                <VehicleFlag vehicle={vehicleDetail} />
              </MarginBox>
              {vehicleDetail.vehicleIdentifiedBy === vehicleDetail.vrn && (
                <Text type="base" data-cy={'vrn'}>
                  {vehicleDetail?.vrn}
                </Text>
              )}
            </Flex>
            {vehicleDetail?.vin && (
              <Flex direction={'row'}>
                <Text type="sm" color="grey35" data-cy={'vin'}>
                  {`${t('catalog.vehicle.vin', 'VIN')}: ${vehicleDetail.vin}`}
                </Text>
              </Flex>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const setOrderMark = (value: string) => {
    dispatch(
      updateOrderMarkRequest({
        basketReferenceType: basketVehicle.vehicleDetail.vehicleKey ? 'VEHICLE' : 'OTHER',
        vehicleKey: basketVehicle.vehicleDetail.vehicleKey,
        externalBasketId: undefined,
        orderMark: value,
      }),
    );
  };

  const hasVinOrVrn = (vehicleDetail: VehicleDetail) => {
    return !!vehicleDetail?.vrn || !!vehicleDetail?.vin;
  };

  return (
    <CartItemWrapper expanded={expanded} data-cy={`cart-items-car-section-${basketVehicle.vehicleDetail.vin}`}>
      <Flex direction={'row'} minWidth={1000}>
        <MarginBox mt={27} ml={36}>
          <Checkbox
            checked={isSelectAllChecked}
            onChange={selectAll}
            data-cy={`cart-vehicle-section-checkbox-${basketVehicle.vehicleDetail.vehicleKey}`}
          />
        </MarginBox>
        <MarginBox mt={18} ml={33}>
          <Flex direction={'column'} data-cy={'cart-car-details'}>
            {hasVinOrVrn(basketVehicle.vehicleDetail) ? vehicleHeader(basketVehicle.vehicleDetail) : bmmVehicleHeader()}
          </Flex>
        </MarginBox>
        {commercialLink?.canPlaceUrgentOrders ? (
          <Flex justify={'flex-end'}>
            <MarginBox mr={67} mt={26}>
              <UrgentWrapper onClick={selectAllUrgent}>
                <Flex>
                  <Checkbox checked={isSelectAllUrgentChecked} />
                  <Icon
                    mr={5}
                    ml={5}
                    IconComponent={CheckCircleIcon}
                    size={12}
                    display={'inline'}
                    color={theme.color.brand}
                    background={theme.color.brand_black}
                    round
                  />
                  <Text type="base" color="clear_blue">
                    {t('cart.urgent_delivery', 'Urgent delivery')}
                  </Text>
                </Flex>
              </UrgentWrapper>
              <MarginBox mt={1} ml={20}>
                <Text type={'light_dimmer'}>{t('cart.urgent_delivery_note', '(Select for faster delivery)')}</Text>
              </MarginBox>
            </MarginBox>
          </Flex>
        ) : (
          <Flex />
        )}
        <MarginBox mt={13} mr={36}>
          <Flex direction={'row'}>
            <Text type={'highlight'} data-cy={'cart-total-vat-excl-per-vehicle'}>
              {t('common.price.total_VAT_excl', 'Total VAT. Excl')}
            </Text>
          </Flex>
          <Flex direction={'row'}>
            <Text type={'highlight'} data-cy={'section-price-vat-excl'}>
              {textFormatter.formatCurrency(totalPriceForVehicle ?? 0, currency)}
            </Text>
          </Flex>
        </MarginBox>
        <MarginBox mt={27} mr={36}>
          <div onClick={() => setExpanded(!expanded)}>
            <Icon IconComponent={expanded ? AngleUpIcon : AngleDownIcon} />
          </div>
        </MarginBox>
      </Flex>
      {expanded && (
        <Flex direction={'row'}>
          <MarginBox ml={37} mt={17}>
            <SInput
              initialValue={basketVehicle.orderMark}
              maxLength={orderMarkMaxLength}
              onBlur={setOrderMark}
              placeholder={t('order.order_mark', 'Click here to add your order mark')}
              showAlwaysFloatingLabel={basketVehicle?.orderMark ? basketVehicle?.orderMark?.length > 0 : false}
              bordered
              data-cy={'cart-order-mark-input'}
            />
          </MarginBox>
          <MarginBox ml={30} mt={17}>
            <WhiteButton
              data-cy={'cart-browse-catalog-button'}
              onClick={() => {
                const { vehicleDetail } = basketVehicle;
                if (vehicleDetail.country) {
                  params.set(VehicleCountry, vehicleDetail.country);
                }
                fetchKnownVehicle(dispatch, vehicleDetail);
                history.push(
                  `${getCatalogSourceUrl(vehicleDetail.catalogSource)}/${
                    vehicleDetail.vehicleKey
                  }${ROUTER_CATALOG_VEHICLE}?${params}`,
                );
              }}
            >
              {t('catalog.actions.browseCatalog', 'Browse Catalog')}
            </WhiteButton>
          </MarginBox>
        </Flex>
      )}
      {expanded && (
        <Flex direction={'row'}>
          <CartReferenceTable
            vehicleKey={basketVehicle.vehicleDetail?.vehicleKey}
            markRemovedReferences={markRemovedReferences}
            disabledRefs={disabledRefs}
            setDisableRefs={setDisableRefs}
            unsetDisableRef={unsetDisableRef}
            highlightedReference={highlightedReference}
          />
        </Flex>
      )}
    </CartItemWrapper>
  );
};

export default CartItemsVehicle;
